import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { PlatformSubSection } from "@tracabois/template"
import { Action } from "@komune-io/g2-components"
import { Stack, Typography } from "@mui/material"
import { usePage } from "@tracabois/utils"
import { usePopup } from "@tracabois/components"
import { RemovalZone } from "../../../../removalZone/model/removalZone"
import { Realization } from "../../../model/realizationModel"
import { RealizationOriginesTable } from "./RealizationOriginesTable"
import { OriginesAddFormPopup } from "./OriginesAddFormPopup"
import { useRealizationAddRemovalZoneMutation, useRealizationRemoveRemovalZoneMutation } from "../../../api"

interface RealizationOriginesSectionProps {
  realization?: Realization
  onOriginAdded?: () => void
  onOriginRemoved?: () => void
  isLoading?: boolean
  editable?: boolean
}

const useLabels = () => {
  const { t } = useTranslation()
  return useMemo(() => ({
    title: t("realization.origines.title", "Origines"),
    directOrigins: t("realization.origines.direct", "Origines directes"),
    ouvrageOrigins: t("realization.origines.ouvrages", "Origines provenant des ouvrages"),
    addOrigins: t("realization.origines.add", "Ajouter des origines"),
  }), [t])
}

export const RealizationOriginesSection = (props: RealizationOriginesSectionProps) => {
  const { realization, onOriginAdded, onOriginRemoved, isLoading, editable } = props
  const labels = useLabels()
  const popup = usePopup()
  const [addRemovalZoneMutation] = useRealizationAddRemovalZoneMutation()
  const [removeRemovalZoneMutation] = useRealizationRemoveRemovalZoneMutation()

  // Override zones from the realization (already resolved by backend)
  const overrideZones: RemovalZone[] = useMemo(() =>
    realization?.removalZoneOverrides ?? [],
    [realization?.removalZoneOverrides]
  )

  // Origins from ouvrages (deduplicated by id)
  const ouvrageOrigins = useMemo(() => {
    const originMap = new Map<string, RemovalZone>()
    realization?.ouvrages?.forEach((ouvrage) => {
      ouvrage.removalZones?.forEach((zone) => {
        if (zone.id && !originMap.has(zone.id)) {
          originMap.set(zone.id, zone)
        }
      })
    })
    return Array.from(originMap.values())
  }, [realization?.ouvrages])

  const directOriginsPage = usePage<RemovalZone>(overrideZones)
  const ouvrageOriginsPage = usePage<RemovalZone>(ouvrageOrigins)

  const hasOuvrageOrigins = ouvrageOrigins.length > 0

  const handleAddRemovalZone = useCallback(async (removalZoneId: string) => {
    if (!realization?.id) return
    await addRemovalZoneMutation({ id: realization.id, removalZoneId: { id: removalZoneId } })
    onOriginAdded?.()
    popup.close()
  }, [realization?.id, addRemovalZoneMutation, onOriginAdded, popup])

  const handleRemoveZone = useCallback(async (zone: RemovalZone) => {
    if (!realization?.id) return
    await removeRemovalZoneMutation({ id: realization.id, removalZoneId: { id: zone.id } })
    onOriginRemoved?.()
  }, [realization?.id, removeRemovalZoneMutation, onOriginRemoved])

  const actions: Action[] = [{
    key: "addOrigine",
    label: labels.addOrigins,
    showIf: () => editable || false,
    onClick: popup.open,
  }]

  return (
    <PlatformSubSection title={labels.title} actions={actions}>
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            {labels.directOrigins}
          </Typography>
          <RealizationOriginesTable
            page={directOriginsPage.page}
            isLoading={isLoading}
            editable={editable}
            onPageQueryChange={directOriginsPage.setPageQuery}
            onRemoveZone={editable ? handleRemoveZone : undefined}
          />
        </Stack>

        {hasOuvrageOrigins && (
          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              {labels.ouvrageOrigins}
            </Typography>
            <RealizationOriginesTable
              page={ouvrageOriginsPage.page}
              isLoading={isLoading}
              onPageQueryChange={ouvrageOriginsPage.setPageQuery}
            />
          </Stack>
        )}
      </Stack>

      {editable && realization?.id && popup.isOpen && (
        <OriginesAddFormPopup
          open={popup.isOpen}
          onClose={popup.close}
          realizationId={realization.id}
          existingZoneIds={realization?.removalZoneOverrideIds ?? []}
          onAddRemovalZone={handleAddRemovalZone}
        />
      )}
    </PlatformSubSection>
  )
}
